/*
 * Quick add for product cards on touch devices and small screens.
 *
 * snippets/quick-add.liquid renders <quick-add> inside the card image with the product's variants as JSON
 * (theme.QuickAdd.markup() does the same for the recently viewed cards built in theme.js). This element
 * builds a round "+" button that opens a size row over the bottom of the image. Tapping a size posts to
 * /cart/add.js and dispatches the same bubbling `ajaxProduct:added` event as theme.AjaxProduct, so the cart
 * drawer opens, the cart count updates and every existing listener runs as it does on the product page.
 *
 * Styles live in assets/quick-add.css. The element stays hidden until this script defines it, so without JS
 * nothing shows; without the stylesheet the script removes it rather than show an unstyled row.
 */
(function () {
  if (!window.customElements || customElements.get('quick-add')) {
    return;
  }

  window.theme = window.theme || {};

  var openElement = null;
  var uid = 0;
  var stylesReady = null;

  var icons = {
    plus: '<svg class="quick-add__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 3v14M3 10h14"/></svg>',
    close:
      '<svg class="quick-add__icon quick-add__icon--close" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15"/></svg>',
  };

  // quick-add.css sets --quick-add-ready. Without it the size row would render as plain text under the card
  // link's overlay, where it can't be tapped, so show nothing instead. Checked once per page.
  function stylesLoaded(el) {
    if (stylesReady === null) {
      stylesReady = getComputedStyle(el).getPropertyValue('--quick-add-ready').trim() === '1';
    }
    return stylesReady;
  }

  function string(key, fallback) {
    return (theme.strings && theme.strings[key]) || fallback;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Product data comes from Liquid (options: ["Size", ...]) or from /products/<handle>.js (options: [{ name }]).
  function normalise(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) {
      return null;
    }

    return {
      title: product.title || '',
      options: (product.options || []).map(function (option) {
        return typeof option === 'string' ? option : option.name;
      }),
      variants: product.variants.map(function (variant) {
        return {
          id: variant.id,
          available: variant.available !== false,
          options: variant.options || [],
        };
      }),
    };
  }

  class QuickAdd extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!stylesLoaded(this) || (!this.panel && !this.build())) {
        this.remove();
        return;
      }

      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.close(false);
    }

    build() {
      var script = this.querySelector('script[type="application/json"]');
      var data;

      try {
        data = normalise(JSON.parse(script.textContent));
      } catch (error) {
        return false;
      }

      if (
        !data ||
        !data.variants.some(function (variant) {
          return variant.available;
        })
      ) {
        return false;
      }

      // A copied card (a slider clone, say) arrives with markup already built: rebuild it with a fresh id.
      this.querySelectorAll('.quick-add__toggle, .quick-add__panel').forEach(function (el) {
        el.remove();
      });

      var id = 'QuickAdd-' + ++uid;
      var variants = data.variants;
      var single = variants.length === 1;

      // The options that differ between variants are the ones the shopper picks (Size on this store).
      var picked = data.options
        .map(function (name, index) {
          var values = variants.map(function (variant) {
            return variant.options[index];
          });
          return {
            name: name,
            index: index,
            varies: values.some(function (value) {
              return value !== values[0];
            }),
          };
        })
        .filter(function (option) {
          return option.varies;
        });

      var label;
      var optionsMarkup;

      if (single) {
        var sizeName = string('sizeTrigger', 'Size').toLowerCase();
        var sizeIndex = data.options.findIndex(function (name) {
          return String(name).toLowerCase() === sizeName;
        });
        label = sizeIndex > -1 ? variants[0].options[sizeIndex] || '' : '';
        optionsMarkup =
          '<button type="button" class="quick-add__option quick-add__option--full" data-variant-id="' +
          variants[0].id +
          '">' +
          escapeHtml(string('addToCart', 'Add to cart')) +
          '</button>';
      } else {
        label = string('quickAddSelect', 'Select [option]').replace(
          '[option]',
          picked
            .map(function (option) {
              return option.name;
            })
            .join(' / ') || data.options.join(' / '),
        );
        optionsMarkup = variants
          .map(function (variant) {
            var text = picked.length
              ? picked
                  .map(function (option) {
                    return variant.options[option.index];
                  })
                  .join(' / ')
              : variant.options.join(' / ');

            if (!variant.available) {
              return (
                '<button type="button" class="quick-add__option" disabled>' +
                escapeHtml(text) +
                '<span class="visually-hidden"> ' +
                escapeHtml(string('soldOut', 'Sold out')) +
                '</span></button>'
              );
            }

            return (
              '<button type="button" class="quick-add__option" data-variant-id="' +
              variant.id +
              '">' +
              escapeHtml(text) +
              '</button>'
            );
          })
          .join('');
      }

      var cartAddUrl = (theme.routes && theme.routes.cartAdd ? theme.routes.cartAdd : '/cart/add.js').replace(
        /\.js$/,
        '',
      );

      this.insertAdjacentHTML(
        'beforeend',
        '<button type="button" class="quick-add__toggle" aria-expanded="false" aria-controls="' +
          id +
          '" aria-label="' +
          escapeHtml(string('quickAdd', 'Quick add') + ': ' + data.title) +
          '">' +
          icons.plus +
          '</button>' +
          '<form class="quick-add__panel" id="' +
          id +
          '" action="' +
          escapeHtml(cartAddUrl) +
          '" method="post">' +
          '<input type="hidden" name="id" value="">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<div class="quick-add__head">' +
          '<span class="quick-add__label">' +
          escapeHtml(label) +
          '</span>' +
          '<button type="button" class="quick-add__close" aria-label="' +
          escapeHtml(string('close', 'Close')) +
          '">' +
          icons.close +
          '</button>' +
          '</div>' +
          '<div class="quick-add__options" role="group" aria-label="' +
          escapeHtml(label || data.title) +
          '">' +
          optionsMarkup +
          '</div>' +
          '<p class="quick-add__error" role="alert" hidden></p>' +
          '</form>',
      );

      this.toggle = this.querySelector('.quick-add__toggle');
      this.panel = this.querySelector('.quick-add__panel');
      this.idInput = this.panel.querySelector('[name="id"]');
      this.errorEl = this.panel.querySelector('.quick-add__error');

      return true;
    }

    onClick(evt) {
      var button = evt.target.closest('button');

      if (!button || !this.contains(button)) {
        return;
      }

      if (button === this.toggle) {
        this.open();
      } else if (button.classList.contains('quick-add__close')) {
        this.close(true);
      } else if (button.dataset.variantId) {
        this.add(button);
      }
    }

    onDocumentClick(evt) {
      if (!this.contains(evt.target)) {
        this.close(false);
      }
    }

    onKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close(true);
      }
    }

    open() {
      if (openElement && openElement !== this) {
        openElement.close(false);
      }

      openElement = this;
      this.hideError();
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');

      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);

      var first = this.panel.querySelector('.quick-add__option:not([disabled])');
      if (first) {
        first.focus({ preventScroll: true });
      }
    }

    close(returnFocus) {
      if (openElement === this) {
        openElement = null;
      }

      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);

      if (!this.classList.contains('is-open')) {
        return;
      }

      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');

      if (returnFocus) {
        this.toggle.focus({ preventScroll: true });
      }
    }

    add(button) {
      if (this.busy) {
        return;
      }

      this.idInput.value = button.dataset.variantId;
      this.hideError();

      // With the cart set to "Page" the product form posts normally and lands on /cart; do the same.
      if (!theme.settings || theme.settings.cartType !== 'drawer') {
        this.panel.submit();
        return;
      }

      this.setBusy(true, button);

      fetch(theme.routes.cartAdd, {
        method: 'POST',
        body: new URLSearchParams(new FormData(this.panel)).toString(),
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(
          function (result) {
            this.setBusy(false);

            if (!result.ok || result.data.status) {
              this.fail(result.data.description || result.data.message);
              return;
            }

            this.added(result.data, button);
          }.bind(this),
        )
        .catch(
          function () {
            this.setBusy(false);
            this.fail();
          }.bind(this),
        );
    }

    added(item, button) {
      this.close(false);

      // Same event and detail as theme.AjaxProduct: CartDrawer rebuilds the cart and opens.
      this.panel.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { product: item, addToCartBtn: button },
          bubbles: true,
        }),
      );

      // The cart page has no drawer: reload it to show the new line, as theme.AjaxProduct does.
      if (document.body.classList.contains('template-cart')) {
        window.scrollTo(0, 0);
        location.reload();
      } else if (!document.getElementById('CartDrawer')) {
        location.href = theme.routes.cartPage || '/cart';
      }
    }

    fail(message) {
      this.errorEl.textContent = message || string('quickAddError', 'Something went wrong. Please try again.');
      this.errorEl.hidden = false;

      this.panel.dispatchEvent(
        new CustomEvent('ajaxProduct:error', {
          detail: { errorMessage: this.errorEl.textContent },
          bubbles: true,
        }),
      );
    }

    hideError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }

    setBusy(busy, button) {
      this.busy = busy;
      this.panel.classList.toggle('is-busy', busy);
      this.panel.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.panel.querySelectorAll('.quick-add__option').forEach(function (option) {
        option.classList.toggle('is-loading', busy && option === button);
      });
    }
  }

  // Markup for cards built in JS (theme.buildProductGridItem, used by Recently viewed).
  theme.QuickAdd = {
    markup: function (product) {
      if (
        !theme.settings ||
        !theme.settings.quickAdd ||
        !product ||
        product.available === false ||
        product.requires_selling_plan
      ) {
        return '';
      }

      var data = normalise(product);
      if (!data) {
        return '';
      }

      var json = JSON.stringify(data).replace(/</g, '\\u003c');
      return '<quick-add class="quick-add"><script type="application/json">' + json + '</script></quick-add>';
    },
  };

  customElements.define('quick-add', QuickAdd);
})();
